/**
 * The ONE place minutes and days become text on the TimeRocket page. Every table, tile and
 * modal here formats through these two, so "168:00" in the KPI row and "168:00" in the table
 * footer can never disagree by a rounding rule.
 */

/** Signed h:mm from raw minutes: 10080 → "168:00", -225 → "-3:45", 0 → "0:00". */
export function formatMinutes(min: number | null | undefined): string {
    if (min === null || min === undefined || !Number.isFinite(min)) return '—';
    const abs = Math.round(Math.abs(min));
    const sign = min < 0 && abs > 0 ? '-' : ''; // -0.4 rounds to 0:00, not -0:00
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `${sign}${h}:${String(m).padStart(2, '0')}`;
}

/** Days with ONE decimal, Swiss formatting: 12.5 → "12.5", -0.25 → "-0.3". */
export function formatDays(d: number | null | undefined): string {
    if (d === null || d === undefined || !Number.isFinite(d)) return '—';
    return new Intl.NumberFormat('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(d);
}

/**
 * Raw minutes shown as DAYS through the person's conversion rate: 1008 min at 504 min/day →
 * "2.0". Without a rate there is no honest day figure (trap 13), so "—" — never a guess at
 * 8:24. The absences table and the vacation plan use this for every duration column, so a
 * Feiertag day and a Ferien day are converted by the same rule.
 */
export function formatMinutesAsDays(min: number | null | undefined, conversionRate: number | null | undefined): string {
    if (min === null || min === undefined || !Number.isFinite(min)) return '—';
    if (conversionRate === null || conversionRate === undefined || !Number.isFinite(conversionRate) || conversionRate <= 0) return '—';
    return formatDays(min / conversionRate);
}

/** The numeric counterpart of formatMinutesAsDays for sorting and filtering; null without a rate. */
export function minutesAsDays(min: number | null | undefined, conversionRate: number | null | undefined): number | null {
    if (min === null || min === undefined || !Number.isFinite(min)) return null;
    if (conversionRate === null || conversionRate === undefined || !Number.isFinite(conversionRate) || conversionRate <= 0) return null;
    return min / conversionRate;
}

/** A ratio as a percentage with ONE decimal: 0.0342 → "3.4 %"; null → "—" (undefined, not zero). */
export function formatPercent(ratio: number | null | undefined): string {
    if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—';
    return `${new Intl.NumberFormat('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(ratio * 100)} %`;
}

/** "60 %" — the workload percentage as the contract carries it. */
export function formatWorkload(pct: number | null | undefined): string {
    if (pct === null || pct === undefined || !Number.isFinite(pct)) return '—';
    return `${new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(pct)} %`;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Parse a YYYY-MM-DD key as a LOCAL date — never `new Date(iso)`, which reads it as UTC. */
export function parseDateKey(key: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** "4 Sep 2026" (en-GB order, no locale dependency). */
export function formatDateLong(key: string | null | undefined): string {
    if (!key) return '—';
    const d = parseDateKey(key);
    if (!d) return key;
    return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Thu" for the weekday of a YYYY-MM-DD key. */
export function weekdayShort(key: string): string {
    const d = parseDateKey(key);
    return d ? WEEKDAYS_SHORT[d.getDay()] : '';
}

/** "1 Sep 2026 – 4 Sep 2026" */
export function formatRange(from: string, to: string): string {
    return `${formatDateLong(from)} – ${formatDateLong(to)}`;
}
